import * as React from 'react';

import { InjectedTranslateProps, translate } from 'react-i18next';

import { AvatarSize } from 'wix-ui-tpa/Avatar';
import { Text, TYPOGRAPHY } from 'wix-ui-tpa/Text';
import { TEXT_BUTTON_PRIORITY, TextButton } from 'wix-ui-tpa/TextButton';

import { UserCard } from '@wix/social-groups-common';
import { compose } from '@wix/social-groups-common/dist/src/compose';
import {
  canInviteMembers,
  isGroupMember,
  memberWrapper,
} from '@wix/social-groups-api';
import { ApiTypes } from '@wix/social-groups-api/dist/src/types';
import { WithGroup, WithGroupProps, withSiteMembers } from '../../Context';

import styles from './MembersWidget.st.css';
import { WithSiteMembers } from '../../Context/withSiteMembers';
import {
  BIUserEntry,
  tryToCallBi,
  withBiLogger,
  WithBiLoggerProps,
} from '@wix/social-groups-common/dist/src/context';

function memberFilters(currentMember: ApiTypes.v1.GroupMemberResponse) {
  return {
    isNotCurrentUser(member: ApiTypes.v1.GroupMemberResponse) {
      return currentMember.siteMemberId !== member.siteMemberId;
    },
    isNotGroupMember(member: ApiTypes.v1.GroupMemberResponse) {
      return member.relationship === ApiTypes.v1.RelationshipWithGroup.NONE;
    },
    shuffle() {
      return Math.random() - 0.5;
    },
  };
}

export class MembersListComponent extends React.Component<
  InjectedTranslateProps & WithGroupProps & WithSiteMembers & WithBiLoggerProps
> {
  handleMemberAddClick(siteMemberId: string) {
    const { biLogger, group } = this.props;
    return () => {
      tryToCallBi(async () => {
        await biLogger.groupsAddMemberClicked({
          groupId: group.groupId,
          origin: 'widget_plus_add_btn',
          userEntry: BIUserEntry.SITE,
        });
      });
      this.props.addMember(this.props.group, siteMemberId);
    };
  }

  handleMemberFollowClick(siteMemberId: string) {
    const { biLogger, group } = this.props;
    return () => {
      tryToCallBi(async () => {
        await biLogger.followMember({
          group_id: group.groupId,
          site_member_id: siteMemberId,
          origin: 'discussion_tab_link_clk',
          userEntry: BIUserEntry.SITE,
          action: 'follow',
        });
      });
    };
  }

  navigateToUserProfile(siteMemberId: string) {
    return () => this.props.openUserProfile(siteMemberId);
  }

  render() {
    const {
      t,
      members: groupMembers,
      currentMember,
      siteMembers,
      group,
    } = this.props;

    const canInvite = canInviteMembers(group);
    const filters = memberFilters(currentMember);
    let filtered: ApiTypes.v1.GroupMemberResponse[];

    if (canInvite) {
      filtered = siteMembers
        .filter(filters.isNotCurrentUser)
        .filter(filters.isNotGroupMember)
        .sort(filters.shuffle)
        .slice(0, 5);
    } else {
      filtered = groupMembers
        .filter(isGroupMember)
        .filter(filters.isNotCurrentUser)
        .sort(filters.shuffle)
        .slice(0, 5);
    }

    if (!filtered.length) {
      return (
        <Text
          tagName="p"
          typography={TYPOGRAPHY.runningText}
          {...styles(styles.empty)}
        >
          {t('groups-web.discussion.members.empty')}
        </Text>
      );
    }

    return (
      <>
        {filtered.map(m => {
          const member = memberWrapper(m);
          return (
            <div className={styles.memberWrapper} key={member.siteMemberId}>
              <UserCard
                name={member.name.nick || t('groups-web.member.anonymous')}
                pic={member.imageUrl}
                avatarSize={AvatarSize.small}
                onUserClick={this.navigateToUserProfile(member.siteMemberId)}
                userAction={
                  <TextButton
                    onClick={
                      canInvite
                        ? this.handleMemberAddClick(member.siteMemberId)
                        : this.handleMemberFollowClick(member.siteMemberId)
                    }
                    priority={TEXT_BUTTON_PRIORITY.primary}
                  >
                    {canInvite ? t('groups-web.add') : t('groups-web.follow')}
                  </TextButton>
                }
              />
            </div>
          );
        })}
      </>
    );
  }
}

const enhance = compose(translate(), WithGroup, withSiteMembers, withBiLogger);

export const MembersList = enhance(MembersListComponent);
